import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiPlus, FiAlertTriangle, FiCheck, FiX, FiSearch, FiDownload } from 'react-icons/fi';
import { formatDistanceToNow, isPast } from 'date-fns';
import { exportToExcel } from '../../utils/exportExcel';
import { useAuth } from '../../context/AuthContext';
import { visaService, userService } from '../../services/api';
import VisaForm from './VisaForm';
import './VisaManagement.css';

const VisaManagement = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [visas, setVisas] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [selectedVisa, setSelectedVisa] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('expiryDate');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isAdmin) {
        // Admin view: fetch all visas
        const visasRes = await visaService.getAll({ limit: 1000 });
        setVisas(visasRes.data.visaDetails || []);

        // Fetch all users for admin view
        const usersRes = await userService.getAll({ limit: 1000 });
        setEmployees(usersRes.data.users || []);
      } else {
        // Employee view: fetch their own visa
        const myVisaRes = await visaService.getMy();
        if (myVisaRes.data) {
          setVisas([myVisaRes.data]);
        } else {
          setVisas([]);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load visa information');
      console.error('Error fetching visa data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getVisaStatus = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.floor((expiry - today) / (1000 * 60 * 60 * 24));

    if (isPast(expiry)) {
      return { status: 'expired', label: 'Expired', days: daysUntilExpiry };
    }
    if (daysUntilExpiry <= 30) {
      return { status: 'expiring', label: 'Expiring Soon', days: daysUntilExpiry };
    }
    return { status: 'active', label: 'Active', days: daysUntilExpiry };
  };

  const getEmployeeName = (userId) => {
    const emp = employees.find(e => e.id === userId);
    return emp ? `${emp.firstName} ${emp.lastName}` : 'Unknown';
  };

  const maskVisaNumber = (number) => {
    if (!number) return '****';
    return `****${number.slice(-4)}`;
  };

  const handleEdit = (visa) => {
    setSelectedVisa(visa);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setSelectedVisa(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setSelectedVisa(null);
  };

  const handleFormSave = async () => {
    await fetchData();
    handleFormClose();
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedVisas = visas
    .filter(visa => {
      if (filterStatus !== 'all') {
        const status = getVisaStatus(visa.expiryDate).status;
        if (status !== filterStatus) return false;
      }

      if (searchQuery && isAdmin) {
        const employeeName = getEmployeeName(visa.userId).toLowerCase();
        if (!employeeName.includes(searchQuery.toLowerCase())) return false;
      }

      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;

      switch (sortColumn) {
        case 'name':
          aVal = getEmployeeName(a.userId).toLowerCase();
          bVal = getEmployeeName(b.userId).toLowerCase();
          break;
        case 'type':
          aVal = a.visaType.toLowerCase();
          bVal = b.visaType.toLowerCase();
          break;
        case 'expiryDate':
          aVal = new Date(a.expiryDate);
          bVal = new Date(b.expiryDate);
          break;
        case 'status':
          aVal = getVisaStatus(a.expiryDate).label.toLowerCase();
          bVal = getVisaStatus(b.expiryDate).label.toLowerCase();
          break;
        case 'country':
          aVal = a.countryOfOrigin.toLowerCase();
          bVal = b.countryOfOrigin.toLowerCase();
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  if (loading) {
    return (
      <div className="visa-management">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading visa information...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin && visas.length === 0) {
    return (
      <div className="visa-management">
        <div className="visa-empty-state">
          <FiAlertTriangle size={48} className="empty-icon" />
          <h2>No Visa Details Found</h2>
          <p>Add your visa information to keep your right-to-work status current.</p>
          <button className="btn btn-primary" onClick={handleAddNew}>
            <FiPlus /> Add Visa Details
          </button>
        </div>
        {showForm && (
          <VisaForm
            visa={selectedVisa}
            onClose={handleFormClose}
            onSave={handleFormSave}
          />
        )}
      </div>
    );
  }

  if (!isAdmin && visas.length > 0) {
    const visa = visas[0];
    const statusInfo = getVisaStatus(visa.expiryDate);

    return (
      <div className="visa-management employee-view">
        <div className="visa-header">
          <h1>Visa Information</h1>
          <button className="btn btn-primary" onClick={handleAddNew}>
            <FiEdit2 /> Update Visa
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        <div className="visa-detail-card">
          <div className="visa-card-header">
            <div>
              <h2>{visa.visaType}</h2>
              <p className="visa-number">Visa #: {maskVisaNumber(visa.visaNumber)}</p>
            </div>
            <div className={`visa-status-badge ${statusInfo.status}`}>
              {statusInfo.label}
            </div>
          </div>

          <div className="visa-details-grid">
            <div className="visa-detail-item">
              <span className="label">Issue Date</span>
              <span className="value">
                {new Date(visa.issueDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>

            <div className="visa-detail-item">
              <span className="label">Expiry Date</span>
              <span className="value">
                {new Date(visa.expiryDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
            </div>

            <div className="visa-detail-item">
              <span className="label">Country</span>
              <span className="value">{visa.countryOfOrigin}</span>
            </div>

            <div className="visa-detail-item">
              <span className="label">Right to Work Check Date</span>
              <span className="value">
                {visa.rightToWorkCheckDate
                  ? new Date(visa.rightToWorkCheckDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : 'Not set'}
              </span>
            </div>

            <div className="visa-detail-item">
              <span className="label">Days Until Expiry</span>
              <span className={`value countdown ${statusInfo.status}`}>
                {statusInfo.days < 0
                  ? `${Math.abs(statusInfo.days)} days expired`
                  : `${statusInfo.days} days remaining`}
              </span>
            </div>
          </div>

          {visa.notes && (
            <div className="visa-notes">
              <p className="label">Notes</p>
              <p className="value">{visa.notes}</p>
            </div>
          )}

          <button className="btn btn-secondary" onClick={handleAddNew}>
            <FiEdit2 /> Edit Visa Information
          </button>
        </div>

        {showForm && (
          <VisaForm
            visa={selectedVisa}
            onClose={handleFormClose}
            onSave={handleFormSave}
          />
        )}
      </div>
    );
  }

  // Admin view: table
  return (
    <div className="visa-management admin-view">
      <div className="visa-header">
        <h1>Employee Visa Management</h1>
        <div className="header-controls">
          <button className="btn btn-secondary" onClick={() => {
            const data = visas.map(v => ({
              Employee: v.User ? `${v.User.firstName} ${v.User.lastName}` : '',
              Email: v.User?.email || '',
              'Visa Type': v.visaType,
              'Visa Number': v.visaNumber || '',
              'Issue Date': v.issueDate || '',
              'Expiry Date': v.expiryDate || '',
              Status: v.status,
              'Country of Origin': v.countryOfOrigin || '',
              Notes: v.notes || '',
            }));
            exportToExcel(data, 'Visa_Details');
          }}>
            <FiDownload /> Export Excel
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="visa-filters">
        <div className="search-box">
          <FiSearch />
          <input
            type="text"
            placeholder="Search by employee name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="expiring">Expiring Soon</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {filteredAndSortedVisas.length === 0 ? (
        <div className="no-results">
          <p>No visas found matching your criteria.</p>
        </div>
      ) : (
        <div className="visa-table-wrapper">
          <table className="visa-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className="sortable">
                  Name {sortColumn === 'name' && <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </th>
                <th onClick={() => handleSort('type')} className="sortable">
                  Visa Type {sortColumn === 'type' && <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </th>
                <th onClick={() => handleSort('expiryDate')} className="sortable">
                  Expiry Date {sortColumn === 'expiryDate' && <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </th>
                <th onClick={() => handleSort('status')} className="sortable">
                  Status {sortColumn === 'status' && <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </th>
                <th onClick={() => handleSort('country')} className="sortable">
                  Country {sortColumn === 'country' && <span className="sort-indicator">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </th>
                <th>Days Remaining</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedVisas.map((visa) => {
                const statusInfo = getVisaStatus(visa.expiryDate);
                return (
                  <tr
                    key={visa.id}
                    className={`visa-row ${statusInfo.status}`}
                    onClick={() => handleEdit(visa)}
                  >
                    <td>{getEmployeeName(visa.userId)}</td>
                    <td>{visa.visaType}</td>
                    <td>
                      {new Date(visa.expiryDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td>
                      <span className={`status-badge ${statusInfo.status}`}>
                        {statusInfo.label}
                      </span>
                    </td>
                    <td>{visa.countryOfOrigin}</td>
                    <td className={`days-remaining ${statusInfo.status}`}>
                      {statusInfo.days < 0 ? (
                        <>
                          <FiX size={16} /> {Math.abs(statusInfo.days)}
                        </>
                      ) : (
                        <>
                          <FiCheck size={16} /> {statusInfo.days}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <VisaForm
          visa={selectedVisa}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default VisaManagement;
